import { lazy, type ComponentType } from "react";

// Stale-chunk recovery.
//
// Routes are code-split with React.lazy(() => import(...)), so each maps to its
// own hashed Vite chunk. A prod deploy uploads the new hashes and DELETES the
// old ones (`s3 sync --delete`). A tab opened before the deploy still holds an
// index referencing the old hashes, so the next navigation to a not-yet-loaded
// route makes import() 404 — the lazy boundary throws and the app falls back to
// the error screen ("観測を一時的に見失いました"). A full reload fixes it because
// index.html is served `no-cache`, so the browser revalidates and gets the new
// hashes.
//
// We do that reload automatically, exactly once: on the first chunk failure we
// set a session flag and reload; if it STILL fails after the reload (flag
// already set) we surface the error instead of reloading again, so a genuinely
// broken build can't trap the user in a reload loop.

const RELOAD_FLAG = "ace.chunkReloaded";

// A failed dynamic import / stale module-preload. Engines phrase it differently.
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return (
    /dynamically imported module/i.test(msg) || // Chrome/Vite
    /Importing a module script failed/i.test(msg) || // Safari
    /error loading dynamically imported module/i.test(msg) || // Firefox
    /ChunkLoadError/i.test(msg)
  );
}

// Reload the page once to pick up the freshly-deployed index + chunks. Returns
// true if it triggered a reload (caller should keep the loading surface up),
// false if we already reloaded once (caller should surface the error).
export function reloadOnceForStaleChunk(): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return false;
    sessionStorage.setItem(RELOAD_FLAG, "1");
  } catch {
    // sessionStorage can throw (private mode / disabled). Without a guard we
    // could loop, so bail rather than risk it.
    return false;
  }
  window.location.reload();
  return true;
}

// Any chunk that loads successfully means we're on a good build — clear the
// guard so a LATER deploy is allowed its own one-shot reload.
function clearGuard() {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // ignore — guard is best-effort
  }
}

// Wrap a route import so a stale/failed chunk turns into a one-shot reload
// instead of a hard crash. Exported (not only consumed by lazyWithReload) so the
// recovery contract is unit-testable without reaching into React's lazy
// internals.
export function guardChunkImport<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): Promise<{ default: T }> {
  return factory().then(
    (mod) => {
      // React's lazy contract requires the factory to resolve to a module that
      // actually carries a `default` export. In practice a stale or half-
      // evaluated chunk can resolve *successfully* to `undefined` or a module
      // object missing `default` — WebKit bfcache restores and preloaded-but-
      // empty modules are the observed triggers. React then dereferences the
      // resolved value deep in its internals and throws
      // "undefined is not an object (evaluating 'e._result.default')"
      // (Sentry ACE-FRONTEND-5) with no boundary that can recover it. Treat a
      // defaultless resolve exactly like a rejected import: reload once for a
      // fresh build, else surface a clean ChunkLoadError to the ErrorBoundary.
      if (mod == null || mod.default == null) {
        if (reloadOnceForStaleChunk()) {
          return new Promise<{ default: T }>(() => {});
        }
        throw new Error("ChunkLoadError: module resolved without a default export");
      }
      clearGuard();
      return mod;
    },
    (err: unknown) => {
      // A rejected route import is, in practice, a stale/missing chunk or a
      // transient network failure — both are fixed by reloading. Reload once;
      // if we already did, let it propagate to the ErrorBoundary.
      if (reloadOnceForStaleChunk()) {
        // Never resolve: keep Suspense's loading fallback up while the page
        // navigates away, so the error screen never flashes.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    },
  );
}

// Drop-in replacement for React.lazy that auto-recovers from stale-chunk import
// failures after a deploy. Used via `import { lazyWithReload as lazy }`.
export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() => guardChunkImport(factory));
}
